'use client';

import { useTransition } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { localTodayISODate } from '@kit/shared/date-utils';
import { Button } from '@kit/ui/button';
import { DateField } from '@kit/ui/date-field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { toast } from '@kit/ui/sonner';
import { Spinner } from '@kit/ui/spinner';
import { Textarea } from '@kit/ui/textarea';

const ScheduleMeetingSchema = z.object({
  scheduledAt: z.string().min(1, 'Required'),
  scheduledTime: z.string().min(1, 'Required'),
  // No `.default()` on this or `priority`: `useForm`'s `defaultValues` below already
  // supplies both (30 / 'normal'), and `.default()` only fires on `undefined`, so it
  // never ran. Declaring it made the schema's input type optional and its output type
  // required, which left `zodResolver`'s return unassignable to `useForm<FormData>`.
  durationMinutes: z.coerce.number().min(15).max(480),
  location: z.string().max(500).optional(),
  meetingUrl: z.string().url().optional().or(z.literal('')),
  priority: z.enum(['normal', 'high']),
  notes: z.string().max(2000).optional(),
  organizationNotes: z.string().max(2000).optional(),
});

type FormData = z.infer<typeof ScheduleMeetingSchema>;

interface ScheduleMeetingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  dancerId: string;
  eventId?: string;
  dancerName: string;
  onSubmit: (data: {
    organizationId: string;
    dancerId: string;
    eventId?: string;
    scheduledAt: string;
    durationMinutes: number;
    location?: string;
    meetingUrl?: string;
    priority: 'normal' | 'high';
    notes?: string;
    organizationNotes?: string;
    requestedBy: 'organization' | 'dancer';
  }) => Promise<{ success: boolean; error?: string }>;
  requestedBy?: 'organization' | 'dancer';
}

/**
 * Modal for scheduling a new meeting
 *
 * Features:
 * - Date and time picker
 * - Duration selection
 * - Location (physical or virtual)
 * - Priority setting
 * - Notes for both parties
 */
export function ScheduleMeetingModal({
  open,
  onOpenChange,
  organizationId,
  dancerId,
  eventId,
  dancerName,
  onSubmit,
  requestedBy = 'organization',
}: ScheduleMeetingModalProps) {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();

  const form = useForm<FormData>({
    resolver: zodResolver(ScheduleMeetingSchema),
    defaultValues: {
      scheduledAt: '',
      scheduledTime: '',
      durationMinutes: 30,
      location: '',
      meetingUrl: '',
      priority: 'normal',
      notes: '',
      organizationNotes: '',
    },
  });

  const handleSubmit = (data: FormData) => {
    startTransition(async () => {
      const scheduledAt = new Date(
        `${data.scheduledAt}T${data.scheduledTime}`,
      ).toISOString();

      const result = await onSubmit({
        organizationId,
        dancerId,
        eventId,
        scheduledAt,
        durationMinutes: data.durationMinutes,
        location: data.location || undefined,
        meetingUrl: data.meetingUrl || undefined,
        priority: data.priority,
        notes: data.notes || undefined,
        organizationNotes: data.organizationNotes || undefined,
        requestedBy,
      });

      if (result.success) {
        toast.success(t('profiles:meetings.toast.scheduled'));
        form.reset();
        onOpenChange(false);
      } else {
        toast.error(result.error ?? t('profiles:meetings.toast.error'));
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('profiles:meetings.schedule.title')}</DialogTitle>
          <DialogDescription>
            {t('profiles:meetings.schedule.description', { name: dancerName })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {/* Date and Time */}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="scheduledAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('profiles:meetings.form.date')}</FormLabel>
                    <FormControl>
                      <DateField
                        name={field.name}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        min={localTodayISODate()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="scheduledTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('profiles:meetings.form.time')}</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Duration */}
            <FormField
              control={form.control}
              name="durationMinutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('profiles:meetings.form.duration')}</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    defaultValue={String(field.value)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Location */}
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('profiles:meetings.form.location')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        'profiles:meetings.form.locationPlaceholder',
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('profiles:meetings.form.locationDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Meeting URL */}
            <FormField
              control={form.control}
              name="meetingUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('profiles:meetings.form.meetingUrl')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="https://zoom.us/j/..."
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('profiles:meetings.form.meetingUrlDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Priority */}
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('profiles:meetings.form.priority')}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="normal">
                        {t('profiles:meetings.priority.normal')}
                      </SelectItem>
                      <SelectItem value="high">
                        {t('profiles:meetings.priority.high')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notes (shared) */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('profiles:meetings.form.notes')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('profiles:meetings.form.notesPlaceholder')}
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('profiles:meetings.form.notesDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Organization Notes (private) */}
            {requestedBy === 'organization' && (
              <FormField
                control={form.control}
                name="organizationNotes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('profiles:meetings.form.organizationNotes')}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t(
                          'profiles:meetings.form.organizationNotesPlaceholder',
                        )}
                        className="min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('profiles:meetings.form.organizationNotesDescription')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Spinner className="mr-2 h-4 w-4" />}
                {t('profiles:meetings.schedule.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
